import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OnboardingTour.css';
import { ONBOARDING_REPLAY_EVENT, ONBOARDING_STATUS, getOnboardingKeys, onboardingStorage } from './onboardingStorage';

const SPOTLIGHT_PADDING = 8;
const TARGET_POLL_INTERVAL_MS = 150;
// Some pages (e.g. Client_home) gate their content behind an artificial loading
// delay (ProtectedRoute's ~1.2s guard + an extra ~3s spinner) before a step's
// target mounts. Give the poller enough runway to comfortably outlast that.
const TARGET_POLL_TIMEOUT_MS = 12000;
const TOOLTIP_WIDTH = 320;
const TOOLTIP_GAP = 16;
// The tooltip's real height varies slightly with content length, but a safe estimate
// is enough to decide whether it fits and to clamp it fully on-screen - measuring the
// actual rendered node would need an extra render pass.
const TOOLTIP_EST_HEIGHT = 220;

function computeTooltipStyle(rect, placement = 'bottom') {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Auto-flip to the opposite side when there isn't enough room, so the tooltip never
  // renders clipped off-screen (e.g. a target sitting right below a fixed header).
  let resolvedPlacement = placement;
  if (placement === 'top' && rect.top - TOOLTIP_GAP < TOOLTIP_EST_HEIGHT) {
    resolvedPlacement = 'bottom';
  } else if (placement === 'bottom' && viewportHeight - rect.bottom - TOOLTIP_GAP < TOOLTIP_EST_HEIGHT) {
    resolvedPlacement = 'top';
  } else if (placement === 'left' && rect.left - TOOLTIP_GAP < TOOLTIP_WIDTH) {
    resolvedPlacement = 'right';
  } else if (placement === 'right' && viewportWidth - rect.right - TOOLTIP_GAP < TOOLTIP_WIDTH) {
    resolvedPlacement = 'left';
  }

  let top;
  let left;
  let transform;

  switch (resolvedPlacement) {
    case 'top':
      top = rect.top - TOOLTIP_GAP;
      left = rect.left + rect.width / 2;
      transform = 'translate(-50%, -100%)';
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - TOOLTIP_GAP;
      transform = 'translate(-100%, -50%)';
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + TOOLTIP_GAP;
      transform = 'translate(0, -50%)';
      break;
    case 'bottom':
    default:
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2;
      transform = 'translate(-50%, 0)';
      break;
  }

  // Clamp horizontally so the tooltip never runs off the left/right edges.
  const halfWidth = TOOLTIP_WIDTH / 2;
  if (resolvedPlacement === 'top' || resolvedPlacement === 'bottom') {
    left = Math.min(Math.max(left, halfWidth + TOOLTIP_GAP), viewportWidth - halfWidth - TOOLTIP_GAP);
  } else if (resolvedPlacement === 'left') {
    left = Math.max(left, TOOLTIP_WIDTH + TOOLTIP_GAP);
  } else {
    left = Math.min(left, viewportWidth - TOOLTIP_WIDTH - TOOLTIP_GAP);
  }

  // Clamp vertically based on which way the tooltip grows from its anchor point, so it
  // can never render partially above/below the viewport even in the auto-flipped case.
  if (resolvedPlacement === 'top') {
    top = Math.min(Math.max(top, TOOLTIP_EST_HEIGHT + TOOLTIP_GAP), viewportHeight - TOOLTIP_GAP);
  } else if (resolvedPlacement === 'bottom') {
    top = Math.min(Math.max(top, TOOLTIP_GAP), viewportHeight - TOOLTIP_EST_HEIGHT - TOOLTIP_GAP);
  } else {
    const halfHeight = TOOLTIP_EST_HEIGHT / 2;
    top = Math.min(Math.max(top, halfHeight + TOOLTIP_GAP), viewportHeight - halfHeight - TOOLTIP_GAP);
  }

  return { top, left, transform };
}

// Shown briefly while a step's target is still mounting (e.g. behind a page's own
// loading state), so the user always sees the tooltip/Skip button instead of a
// blank dimmed screen.
const CENTERED_TOOLTIP_STYLE = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

/**
 * Reusable spotlight-style onboarding tour.
 *
 * Mount once at the app-shell level (or scoped to a single page) and pass a
 * config-driven `steps` array:
 *   { target: '[data-onboarding="..."]', title, description, placement, onEnter, onExit }
 *
 * `onEnter`/`onExit` are optional per-step lifecycle hooks - useful for steps whose
 * target lives behind some other UI state (e.g. a collapsed accordion or an inactive
 * tab pane) that needs to be opened before the spotlight can measure/highlight it.
 * `onEnter` fires right before the target is located (forward, backward, or on resume);
 * `onExit` fires when leaving that step - going back, skipping, finishing, or unmounting -
 * so any state it opened can be restored.
 *
 * `namespace` scopes the sessionStorage keys so multiple shells (e.g. different
 * portals) can each run their own independent tour without colliding.
 *
 * `onFinish(status)` is optional and fires after the tour is closed (completed or
 * skipped). Used by the login-only page chain; the "?" replay path does not depend
 * on it.
 */
const OnboardingTour = ({ steps, namespace = 'default', onFinish }) => {
  const keys = useMemo(() => getOnboardingKeys(namespace), [namespace]);
  const [stepIndex, setStepIndex] = useState(null); // null = tour not active
  const [targetRect, setTargetRect] = useState(null);
  const pollRef = useRef(null);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  // Kept up to date every render so cleanup/callbacks (e.g. on unmount) always see the
  // latest steps/stepIndex without needing to be listed as effect dependencies.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;

  // If the tour (or the whole page hosting it) unmounts while a step with an `onExit`
  // hook is active, still run that cleanup so it doesn't leave e.g. a tab pane stuck open.
  useEffect(() => {
    return () => {
      const idx = stepIndexRef.current;
      const activeSteps = stepsRef.current;
      if (idx !== null && activeSteps?.[idx]?.onExit) {
        activeSteps[idx].onExit();
      }
    };
  }, []);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Decide once on mount whether to resume an in-progress tour or start a fresh one.
  useEffect(() => {
    if (!steps || steps.length === 0) return;

    const status = onboardingStorage.getItem(keys.status);

    if (status === ONBOARDING_STATUS.IN_PROGRESS) {
      const savedStep = parseInt(onboardingStorage.getItem(keys.step), 10);
      setStepIndex(Number.isNaN(savedStep) ? 0 : Math.min(Math.max(savedStep, 0), steps.length - 1));
      return;
    }

    if (status === ONBOARDING_STATUS.COMPLETED || status === ONBOARDING_STATUS.SKIPPED) {
      return;
    }

    if (onboardingStorage.getItem(keys.trigger)) {
      onboardingStorage.removeItem(keys.trigger);
      onboardingStorage.setItem(keys.status, ONBOARDING_STATUS.IN_PROGRESS);
      onboardingStorage.setItem(keys.step, '0');
      setStepIndex(0);
    }
    // Intentionally run only once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lets something outside this component (e.g. a header "help" button) replay the same
  // tour on demand, bypassing the one-shot first-login `trigger` check entirely. Reuses
  // the exact same step-rendering flow below - it's just an alternate way to seed step 0.
  useEffect(() => {
    const handleReplay = (event) => {
      if (event.detail?.namespace !== namespace) return;
      if (!steps || steps.length === 0) return;
      onboardingStorage.setItem(keys.status, ONBOARDING_STATUS.IN_PROGRESS);
      onboardingStorage.setItem(keys.step, '0');
      setStepIndex(0);
    };
    window.addEventListener(ONBOARDING_REPLAY_EVENT, handleReplay);
    return () => window.removeEventListener(ONBOARDING_REPLAY_EVENT, handleReplay);
  }, [namespace, steps, keys]);

  const finish = useCallback(
    (status) => {
      clearPoll();
      onboardingStorage.setItem(keys.status, status);
      onboardingStorage.removeItem(keys.step);
      setStepIndex(null);
      setTargetRect(null);
      onFinishRef.current?.(status);
    },
    [clearPoll, keys]
  );

  const goToStep = useCallback(
    (nextIndex) => {
      if (nextIndex < 0) return;
      // Leaving the current step - let it undo whatever its `onEnter` opened
      // (e.g. a tab it switched to) before we move on, whether that's forward,
      // backward, or off the end of the tour.
      if (stepIndex !== null) steps[stepIndex]?.onExit?.();
      if (nextIndex >= steps.length) {
        finish(ONBOARDING_STATUS.COMPLETED);
        return;
      }
      onboardingStorage.setItem(keys.step, String(nextIndex));
      setStepIndex(nextIndex);
    },
    [steps, keys, finish, stepIndex]
  );

  const skip = useCallback(() => {
    if (stepIndex !== null) steps[stepIndex]?.onExit?.();
    finish(ONBOARDING_STATUS.SKIPPED);
  }, [finish, steps, stepIndex]);

  // Locate the active step's target element, retrying briefly in case its page/route
  // is still mounting (e.g. behind a protected-route loading delay).
  useEffect(() => {
    if (stepIndex === null || !steps[stepIndex]) return;

    const step = steps[stepIndex];
    // Run before we try to locate the target, so a step that needs to open a tab/
    // accordion/etc. gets a chance to do so first - the poll below will then keep
    // retrying until that DOM change lands.
    step.onEnter?.();
    clearPoll();
    setTargetRect(null);

    const tryLocate = () => {
      const el = document.querySelector(step.target);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      // Some targets (e.g. a Bootstrap tab pane an `onEnter` hook just switched to)
      // stay mounted in the DOM while inactive/hidden, just with zero layout size -
      // querySelector finds them instantly, before the visibility change has actually
      // painted. Treat that as "not found yet" so we keep polling instead of
      // spotlighting an empty 0x0 box.
      if (rect.width === 0 && rect.height === 0) return false;
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      setTargetRect(rect);
      return true;
    };

    if (tryLocate()) return clearPoll;

    let attempts = 0;
    const maxAttempts = Math.ceil(TARGET_POLL_TIMEOUT_MS / TARGET_POLL_INTERVAL_MS);
    pollRef.current = setInterval(() => {
      attempts += 1;
      const found = tryLocate();
      if (found || attempts >= maxAttempts) {
        clearPoll();
        if (!found) {
          // Target never showed up - don't let the tour get stuck, move along.
          goToStep(stepIndex + 1);
        }
      }
    }, TARGET_POLL_INTERVAL_MS);

    return clearPoll;
  }, [stepIndex, steps, clearPoll, goToStep]);

  // Keep the spotlight/tooltip pinned to the target on scroll/resize.
  useEffect(() => {
    if (stepIndex === null || !steps[stepIndex]) return;
    const step = steps[stepIndex];

    const reposition = () => {
      const el = document.querySelector(step.target);
      if (el) setTargetRect(el.getBoundingClientRect());
    };

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [stepIndex, steps]);

  // Escape key behaves the same as Skip.
  useEffect(() => {
    if (stepIndex === null) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stepIndex, skip]);

  const tooltipStyle = useMemo(() => {
    if (stepIndex === null || !steps[stepIndex]) return null;
    // Target not located yet (e.g. its page is still behind a loading state) - show
    // the tooltip centered rather than leaving the user staring at a blank overlay.
    if (!targetRect) return CENTERED_TOOLTIP_STYLE;
    return computeTooltipStyle(targetRect, steps[stepIndex].placement);
  }, [targetRect, steps, stepIndex]);

  if (stepIndex === null || !steps || !steps[stepIndex]) return null;

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  // Rendered via a portal straight into <body> so the overlay always sits above the
  // entire app, regardless of any z-index/stacking-context quirks in nested ancestors
  // (e.g. the app's fixed header, sidebars, or PrimeReact/Bootstrap layers).
  return createPortal(
    <div className="onboarding-tour-root" role="dialog" aria-modal="true" aria-label="Product tour">
      <div className="onboarding-blocker" onClick={(e) => e.stopPropagation()} />

      {targetRect ? (
        <div
          className="onboarding-spotlight"
          style={{
            top: targetRect.top - SPOTLIGHT_PADDING,
            left: targetRect.left - SPOTLIGHT_PADDING,
            width: targetRect.width + SPOTLIGHT_PADDING * 2,
            height: targetRect.height + SPOTLIGHT_PADDING * 2,
          }}
        />
      ) : (
        <div className="onboarding-dim-full" />
      )}

      {tooltipStyle && (
        <div className="onboarding-tooltip" style={tooltipStyle}>
          <div className="onboarding-tooltip-progress">
            Step {stepIndex + 1} of {steps.length}
          </div>
          <h4 className="onboarding-tooltip-title">{step.title}</h4>
          <p className="onboarding-tooltip-desc">{step.description}</p>
          <div className="onboarding-tooltip-actions">
            <button type="button" className="onboarding-btn onboarding-btn-skip" onClick={skip}>
              Skip
            </button>
            <div className="onboarding-tooltip-nav">
              {stepIndex > 0 && (
                <button
                  type="button"
                  className="onboarding-btn onboarding-btn-secondary"
                  onClick={() => goToStep(stepIndex - 1)}
                >
                  Back
                </button>
              )}
              <button type="button" className="onboarding-btn onboarding-btn-primary" onClick={() => goToStep(stepIndex + 1)}>
                {isLastStep ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default OnboardingTour;
